import { renderRichText } from "gatsby-source-contentful/rich-text"
import { BLOCKS, MARKS, INLINES } from "@contentful/rich-text-types"
import { GatsbyImage, getImage } from "gatsby-plugin-image"

import Prism from "prismjs"

import React, { useEffect, useState, useCallback, useContext } from "react"

import get from "lodash/get"
import { graphql } from "gatsby"

import { Seo } from "../components/seo"
import BlogPostLayout from "../layouts/blog-post/blog-post.layout.jsx"
import LinkCard from "../components/link-card/link-card.component.jsx"
import PostPreviewCard from "../components/post-preview-card/post-preview-card.component"
import Grid from "../components/grid/grid.component"
import MobileContext from "../providers/mobile/mobile.provider"

import {
  BlogPostHero,
  BlogPostBodyContainer,
  BlogPostHeaderContainer,
  BlogPostTitle,
  BlogPostContent,
  SocialContainer,
  AuthorContainer,
  AuthorName,
  AuthorAvatar,
  PostMeta,
  RelatedPostContainer,
  RelatedTitle,
  LinkCardContainer,
  AuthorSeperatorDot,
  PostMetaContent,
  DividerLine,
  PostMetaFlex,
  RelatedPostGrid,
} from "./blog-post.styles"

import "./contentful-html-styling.css"

import SocialIcon from "../components/social-icon/social-icon.component"
import { replaceEmailCTAs } from "../helpers/markdown.helper"
import { BackToTop } from "./cheat-sheets.styles"
import ChevronUp from "../svg/chevron-up.svg"
import ToC from "../components/table-of-contents/table-of-contents.component"
import ArticleCTA from "../components/article-CTA/article-CTA.component.jsx"
import { getCourseData } from "../helpers/coursecard.helper.jsx"
import CareerPathCard from "../components/career-path-card/career-path-card.component.jsx"

// this page use custom meta data
export const Head = ({ location, data }) => {
  const post = data.contentfulBlogPost

  // fallback values are post title and excerpt
  const customMetaData = {
    pageTitle: post.seoPageTitle || post.blogTitle,
    pageDescription: post.seoPageDesc || post.excerpt,
    pageRobots: "index,follow",
  }
  return (
    <Seo
      metaData={customMetaData}
      uri={location.pathname}
      embedOverride={`https:${post.heroImage.file.url}`}
      headScripts={post.headScripts?.childMarkdownRemark.html}
    />
  )
}

const BlogPostTemplate = (props) => {
  const { isMobile } = useContext(MobileContext)
  const links = get(props, "data.contentfulBlogPost.courseLinks")
  const post = get(props, "data.contentfulBlogPost")
  const topCTA = get(props, "data.allContentfulNewEmailCta.nodes")
  const author = get(post, "author")


function transformRichTextWithBackticks(richText) {
  if (!richText || !Array.isArray(richText.content)) {
    return richText // Return original if invalid
  }

  let transformedContent = []

  for (let node of richText.content) {
    if (node.nodeType === "paragraph" && node.content.length > 0) {
      let textNode = node.content[0]

      // Ensure we are processing a text node with value
      if (textNode.nodeType === "text" && typeof textNode.value === "string") {
        let textValue = textNode.value

        // Handle multi-line code blocks (```lang:javascript\ncode\n```)
        if (textValue.startsWith("```") && textValue.endsWith("```")) {
          let match = textValue.match(/^```lang:(\w+)\n/)
          let detectedLanguage = match ? match[1] : "plaintext"

          let cleanedCode = textValue
            .replace(/^```lang:\w+\n/, "")
            .replace(/```$/, "")
            .trim()

          transformedContent.push({
            nodeType: BLOCKS.PARAGRAPH,
            data: {},
            content: [
              {
                nodeType: "text",
                value: `lang:${detectedLanguage}\n${cleanedCode}`,
                marks: [{ type: "code" }], // Apply block-level code mark
                data: {},
              },
            ],
          })

          continue // Skip adding this paragraph
        }

        // Handle inline code blocks (``word`` → word with code mark)
        if (textValue.includes("``")) {
          let parts = textValue.split(/``(.*?)``/g) // Split by inline code pattern
          let newContent = []

          parts.forEach((part, index) => {
            if (index % 2 === 1) {
              // This is an inline code section
              newContent.push({
                nodeType: "text",
                value: `type:inline ${part}`,
                marks: [{ type: "code" }], // Apply inline code mark
                data: {},
              })
            } else if (part) {
              // Regular text
              newContent.push({
                nodeType: "text",
                value: part,
                marks: [],
                data: {},
              })
            }
          })

          transformedContent.push({
            nodeType: "paragraph",
            data: {},
            content: newContent,
          })

          continue // Skip adding this paragraph
        }
      }
    }

    // Keep normal content unchanged
    transformedContent.push(node)
  }

  return { ...richText, content: transformedContent }
}


const parsedContent = JSON.parse(post?.blogContent?.raw || "[]")
const updatedContent = transformRichTextWithBackticks(parsedContent)
if(post.blogContent) post.blogContent.raw = JSON.stringify(updatedContent)

  const body1 = post?.body?.childMarkdownRemark?.html || ""
  // not all blog posts are using body2 markdown field
  const body2 = post?.body2?.childMarkdownRemark?.html || ""
  const bodyContent = body1 + body2

  const [showScrollBtn, setShowScrollBtn] = useState(false)

  const customTOC = post.customToc?.childMarkdownRemark?.html

  const data = {
    versionA: post.blogCtaVersion ?? true, //if null default is true
    title: post.blogCtaTitle,
    desc: post.blogCtaDescription,
    author: author.authorName.split(" ")[0],
    topCta: topCTA,
  }

  const readingTime = get(
    post,
    "childContentfulBlogPostBodyTextNode.childMarkdownRemark.fields.readingTime.text"
  )

  // Function to randomize the input
  function randomize() {
    return Math.random() - 0.5
  }

  const related = get(props, "data.contentfulBlogPost.relatedPosts")

  const wdm = get(props, "data.alias1.nodes")
  // Gets popular posts, randomizes and keeps two posts
  const popular = get(props, "data.allContentfulBlogPost.nodes")
    .sort(randomize)
    .slice(0, 2)
  // Creates and array containing the latest WDM and the selected popular posts, then randomizes them again
  const fallbackPosts = [wdm[0], popular[0], popular[1]].sort(randomize)

  const { twitterLink, linkedinLink, instagramLink } = post.author

  // so that Blog-tag component will link back to the blog page
  const section = "individual"

  const memoizedScrollHandler = useCallback(() => {
    const contentPos = document.getElementById("blog-post-content").offsetTop
    // if no ToC, we don't want to show the 'back to top' button
    if (customTOC && window.scrollY > contentPos - 120) {
      setShowScrollBtn(true)
    } else {
      setShowScrollBtn(false)
    }
  }, 
  // eslint-disable-next-line
  [])
  useEffect(() => {
    // call the highlightAll() function to style our code blocks
    Prism.highlightAll()
  }, [])

  useEffect(() => {
    
    // email CTAs are mounted
    replaceEmailCTAs({
      key: post.slug,
      slug: post.slug,
      tags: post.tags,
      data: data,
      isMonthlyPost: post.isMonthlyPost,
    })
    window.addEventListener("scroll", memoizedScrollHandler)
    return () => window.removeEventListener("scroll", memoizedScrollHandler)
  }, 
  // eslint-disable-next-line
  [post])

  const memoizedTopFunction = useCallback(() => {
    const tocPos = document.getElementById("toc")?.offsetTop // not all blog posts have a ToC
    window.scrollTo({ top: tocPos - 80, behavior: "smooth" })
  }, [])

  const createAuthorLink = (element, skipLink = false) => {
    if (author.isInstructor)
      return (
        <a
          style={{ textUnderlineOffset: "5px" }}
          href={`/about/instructor/${author.authorSlug}`}
          tabIndex={skipLink ? "-1" : "0"}
        >
          {element}
        </a>
      )
    else return element
  }

  const renderPostMeta = () => {
    // This diplays the meta on one line for desktop users
    if (!isMobile) {
      return (
        <PostMeta>
          <PostMetaFlex>
            <PostMetaContent>{post.publishDate}</PostMetaContent>
            <AuthorSeperatorDot />
            {post.lastUpdated && (
              <>
                <PostMetaContent>Updated: {post.lastUpdated}</PostMetaContent>
                <AuthorSeperatorDot />
              </>
            )}
            <PostMetaContent>{readingTime}</PostMetaContent>
          </PostMetaFlex>
        </PostMeta>
      )
    }
    // This displays the meta on one line for mobile users when lastUpdate is null
    else if (isMobile && !post.lastUpdated) {
      return (
        <PostMeta>
          <PostMetaFlex>
            <PostMetaContent>{post.publishDate}</PostMetaContent>
            <AuthorSeperatorDot />
            <PostMetaContent>{readingTime}</PostMetaContent>
          </PostMetaFlex>
        </PostMeta>
      )
    }
    // Otherwise return the multi line mobile view
    else {
      return (
        <PostMeta>
          <PostMetaFlex>
            <PostMetaContent>{post.publishDate}</PostMetaContent>
            <AuthorSeperatorDot />
          </PostMetaFlex>

          <PostMetaFlex mt={"5px"}>
            {post.lastUpdated && (
              <>
                <PostMetaContent>Updated: {post.lastUpdated}</PostMetaContent>
                <AuthorSeperatorDot />
              </>
            )}
            <PostMetaContent>{readingTime}</PostMetaContent>
          </PostMetaFlex>
        </PostMeta>
      )
    }
  }

  const slugifyHeadings = (heading) => {
    // Convert to lowercase,
    // Replace spaces with hyphens,
    // Remove all non-alphanumeric characters except hyphens, including underscores
    return heading
      .toLowerCase()
      .replace(/ /g, "-")
      .replace(/[^\w-]+/g, "")
  }

  // Function to find codeblocks in headers
  const findAndReplaceHeaderCodeBlocks = (text) => {
    // Use regex to find inline code sections
    const parts = text.split(/(``(.*?)``)/g)

    return (
      
        parts.map((part, index) =>
          part.startsWith("``") && part.endsWith("``") ? (
            <>
              <span
                style={{
                  padding: "0 5px 2px",
                  borderRadius: "5px",
                  background: "#636363",
                  color: "#fff",
                }}
              >
                {part.slice(2, -2)}
              </span>{" "}
            </>
          ) : (
            part
          )
        )
      
    )
  }

  // MAIN CONTENT
  const contentSection = () => {
    const parsedContent = JSON.parse(post?.blogContent?.raw || "[]")
    // IF THE BLOGPOST HAS THE NEW RICH TEXT FIELD
    if(post.blogContent && parsedContent.content.length > 1) {
       const options = {
        renderMark: {
          [MARKS.CODE]: (text, s) => {
            if(text.startsWith("type:inline")) {
              text = text.split("type:inline ")[1]
              return (
                <span style={{ padding: "0 5px 2px", borderRadius: "5px", background: "#636363", color: "#fff" }}>
                  {text}
                </span>
              )
            } else {

            const regex = /^lang:(\w+)/
            const languageCheck = regex.exec(text)
            let language = languageCheck ? languageCheck.slice(1) : ["javascript"]
            if(language[0] === "jsx") language[0] = "javascript"

            if (languageCheck) {
              text = text.split("\n").slice(1).join("\n")
            }

            return (
              <div className="gatsby-highlight">
                <pre className=" line-numbers" style={{ paddingLeft: "20px" }}>
                  <code className={`language-${language}`}>
                    {text}
                  </code>
                </pre>
              </div>
            )
          }
          }
        },
         renderNode: {
            [INLINES.HYPERLINK]: node => {
              // Only process youtube links
              if (node.data.uri.includes("youtube.com")) {
                  // Extract videoId from the URL
                  const match = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/.exec(
                      node.data.uri
                  )
                  const videoId =
                      match && match[7].length === 11 ? match[7] : null
                  return (
                      videoId && (
                      <section className="video-container" style={{width: "100%", aspectRatio: "16/9"}}>
                          <iframe
                              className="video"
                              title={`https://youtube.com/embed/${videoId}`}
                              src={`https://youtube.com/embed/${videoId}`}
                              allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
                              frameBorder="0"
                              allowFullScreen
                              width={"100%"}
                              height={"100%"}
                              lazy="true"
                          />
                      </section>
                      )
                  )
              } else {
                // Check if node.data.uri is external or internal
                const isOnPageAnchor = node.data.uri.startsWith("#")
                const target = isOnPageAnchor ? "" : "_blank"
                return (
                  <a
                    href={node.data.uri}
                    target={target}
                    rel="noopener noreferrer"
                  >
                    {node.content[0].value}
                  </a>
                )
              }
            },
            

            [BLOCKS.PARAGRAPH]: (node, children) => {
              const rawText = node.content
                .map((child) => child.value || "")
                .join("");

              if (rawText.includes(`class="email-cta"`)) {
                return (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: rawText,
                    }}
                  />
                );
              }

              return <p>{children}</p>;
          },
           [BLOCKS.EMBEDDED_ENTRY]: (node) => {
             if (node.data.target.__typename === "ContentfulCareerPath") {
                return (
                 <div style={{ maxWidth: "550px", margin: "0 auto" }}>
                 <CareerPathCard
                     key={node.data.target.slug}
                     path={node.data.target}
                   />
                 </div>
               )
             }
             if (node.data.target.__typename === "ContentfulCourseCard") {
               return (
                 <div style={{ maxWidth: "550px", margin: "0 auto" }}>
                   {getCourseData(
                     [node.data.target],
                     {
                       cardImage: "courseImageClick",
                       cardTitle: "courseTitleClick",
                       authorName: "courseAuthorClick",
                       buttonOne: "courseWatchPreviewBtnClick", //Course title
                       buttonTwo: "courseDetailsBtnClick",
                     },
                     "small-course-card"
                   )}
                 </div>
               )
             }
             if (node.data.target.__typename === "ContentfulNewEmailCta") {
               const { blogCtaHeadline, blogCtaDescription } = node.data.target
               return (
                 <ArticleCTA
                   data={data}
                   tags={
                     post.tags &&
                     post.tags
                       .map((tag) => `tag-${tag.value.toUpperCase()}`)
                       .join(", ")
                   }
                   label={blogCtaHeadline}
                   slug={post.slug}
                   isTop={false}
                   isMiddle={false}
                   isMonthlyPost={post.isMonthlyPost}
                   datasets={"datasets"}
                 />
               )
             }
           },
           [BLOCKS.EMBEDDED_ASSET]: (node) => {
             const { gatsbyImageData, description } = node.data.target
             return (
               <GatsbyImage
                 image={getImage(gatsbyImageData)}
                 alt={description}
               />
             )
           },
           [BLOCKS.HEADING_1]: (node, children) => {
             const text = node.content[0].value;
             const slug = slugifyHeadings(text);
             return <h1 id={slug}>{findAndReplaceHeaderCodeBlocks(text)}</h1>;
           },
           [BLOCKS.HEADING_2]: (node, children) => {
              const text = node.content[0].value
              const slug = slugifyHeadings(text)
              return <h2 id={slug}>{findAndReplaceHeaderCodeBlocks(text)}</h2>
           },
           [BLOCKS.HEADING_3]: (node, children) => {
              const text = node.content[0].value
              const slug = slugifyHeadings(text)
              return <h3 id={slug}>{findAndReplaceHeaderCodeBlocks(text)}</h3>
            },
            [BLOCKS.HEADING_4]: (node, children) => {
              const text = node.content[0].value
              const slug = slugifyHeadings(text)
              return <h4 id={slug}>{findAndReplaceHeaderCodeBlocks(text)}</h4>
            },
            [BLOCKS.HEADING_5]: (node, children) => {
              const text = node.content[0].value
              const slug = slugifyHeadings(text)
              return <h5 id={slug}>{findAndReplaceHeaderCodeBlocks(text)}</h5>
            },
            [BLOCKS.HEADING_6]: (node, children) => {
              const text = node.content[0].value
              const slug = slugifyHeadings(text)
              return <h6 id={slug}>{findAndReplaceHeaderCodeBlocks(text)}</h6>
           },
         },
       }
      return (
        <BlogPostContent id="blog-post-content">
          {renderRichText(post.blogContent, options)}
        </BlogPostContent>
      )
    }
    // IF THE BLOGPOST DOES NOT HAVE THE NEW RICH TEXT FIELD
    else {
      return (
        <BlogPostContent
          id="blog-post-content"
          dangerouslySetInnerHTML={{ __html: bodyContent }}
        />
      )
    }
  }

  return (
    <BlogPostLayout mt={[40, 40, 80, 80]} mx={"auto"}>
      <Grid>
        <BlogPostHeaderContainer>
          <BlogPostTitle>{post.blogTitle}</BlogPostTitle>
          <DividerLine />
          <AuthorContainer>
            {createAuthorLink(
              <AuthorAvatar
                src={`${post.author.profileImage.file.url}?h=150&fm=webp`}
                alt={author.authorName}
                width="75"
                height="75"
                loading="lazy"
              />,
              true
            )}

            <div>
              {createAuthorLink(<AuthorName>{author.authorName}</AuthorName>)}
              {renderPostMeta()}
              <SocialContainer>
                {twitterLink ? (
                  <SocialIcon
                    icon={"twitter"}
                    link={twitterLink}
                    tabindex="0"
                  />
                ) : null}

                {instagramLink ? (
                  <SocialIcon
                    icon={"instagram"}
                    link={instagramLink}
                    tabindex="0"
                  />
                ) : null}

                {linkedinLink ? (
                  <SocialIcon
                    icon={"linkedin"}
                    link={linkedinLink}
                    tabindex="0"
                  />
                ) : null}
              </SocialContainer>
            </div>
          </AuthorContainer>
        </BlogPostHeaderContainer>

        <BlogPostBodyContainer>
          <BlogPostHero
            image={post.heroImage.gatsbyImageData}
            alt="hero image"
          />
          {customTOC && <ToC content={customTOC} title={post.tocTitle} />}
      
          {contentSection()}

          <BackToTop pageScrolled={showScrollBtn} onClick={memoizedTopFunction}>
            <ChevronUp />
          </BackToTop>
          <LinkCardContainer>
            {links &&
              links.map((link) => <LinkCard key={link.id} link={link} />)}
          </LinkCardContainer>
        </BlogPostBodyContainer>
      </Grid>
      <RelatedPostContainer className="full-bleed">
        <RelatedTitle>More from Zero To Mastery</RelatedTitle>
        <RelatedPostGrid>
          {related
            ? related.map((post) => (
                <PostPreviewCard
                  key={section + post.slug}
                  className={`${section}Tab`}
                  blogPost={post}
                  section={section}
                  nodatedisplay={true}
                />
              ))
            : fallbackPosts.map((post) => (
                <PostPreviewCard
                  key={section + post.slug}
                  className={`${section}Tab`}
                  blogPost={post}
                  section={section}
                  authorPage={false}
                  nodatedisplay={true}
                />
              ))}
        </RelatedPostGrid>
      </RelatedPostContainer>
    </BlogPostLayout>
  )
}

export default BlogPostTemplate

export const pageQuery = graphql`
  query BlogPostBySlug($slug: String!) {
    allContentfulNewEmailCta {
      nodes {
        blogId
        blogCtaHeadline
        blogCtaDescription {
          childMarkdownRemark {
            html
          }
        }
      }
    }
    alias1: allContentfulBlogPost(
      filter: { tags: { elemMatch: { value: { eq: "WDM" } } } }
      sort: { createdAt: DESC }
      limit: 1
    ) {
      nodes {
        seoPageTitle
        seoPageDesc
        blogTitle
        excerpt
        publishDate(formatString: "MMMM Do, YYYY")
        slug
        author {
          profileImage {
            file {
              url
            }
          }
          authorName
        }
        childContentfulBlogPostBodyTextNode {
          childMarkdownRemark {
            fields {
              readingTime {
                text
              }
            }
          }
        }
        heroImage {
          gatsbyImageData(width: 400)
        }
      }
    }
    allContentfulBlogPost(filter: { popular: { eq: true } }, limit: 6) {
      nodes {
        seoPageTitle
        seoPageDesc
        blogTitle
        excerpt
        publishDate(formatString: "MMMM Do, YYYY")
        slug
        author {
          authorName
          profileImage {
            file {
              url
            }
          }
        }
        childContentfulBlogPostBodyTextNode {
          childMarkdownRemark {
            fields {
              readingTime {
                text
              }
            }
          }
        }
        heroImage {
          gatsbyImageData(width: 400)
        }
      }
    }
    contentfulBlogPost(slug: { eq: $slug }) {
      blogContent {
        raw
        references {
          __typename
          ... on ContentfulAsset {
            contentful_id
            title
            description
            gatsbyImageData(width: 800)
            __typename
          }
          ... on ContentfulNewEmailCta {
            contentful_id
            blogCtaHeadline
            blogCtaDescription {
              blogCtaDescription
            }
            __typename
          }
          ... on ContentfulCourseCard {
            contentful_id
            __typename

            coursePromoUrl
            totalHours
            totalLessons
            courseTitle
            courseExcerpt
            courseUrl
            coursePopularity
            lastUpdatedOn
            filterCategories
            filterTopics
            filterLevels
            courseFormat
            createdAt(formatString: "")
            badge {
              contentfulid
              title
              color
            }
            courseImage {
              gatsbyImageData(placeholder: BLURRED, width: 600)
              file {
                url
              }
            }
            authors {
              id
              authorName
              authorSlug
              profileImage {
                file {
                  url
                }
              }
            }
          }
          ... on ContentfulCareerPath {
            contentful_id
            __typename
            name
            excerpt
            slug
            timelineContent {
              type
            }
            pinnedCtaImage {
              gatsbyImageData(placeholder: BLURRED, width: 400)
            }
          }
        }
      }
      isMonthlyPost
      tocTitle
      customToc {
        childMarkdownRemark {
          html
        }
      }
      headScripts {
        childMarkdownRemark {
          html
        }
      }
      seoPageTitle
      seoPageDesc
      blogTitle
      blogCtaVersion
      blogCtaTitle
      blogCtaDescription {
        childMarkdownRemark {
          html
        }
      }
      heroImage {
        gatsbyImageData(width: 700)
        file {
          url
        }
      }
      excerpt
      slug
      author {
        authorSlug
        isInstructor
        profileImage {
          file {
            url
          }
        }
        authorName
        githubLink
        twitterLink
        linkedinLink
        instagramLink
      }
      tags {
        value
        label
      }
      childContentfulBlogPostBodyTextNode {
        childMarkdownRemark {
          fields {
            readingTime {
              text
            }
          }
        }
      }
      publishDate(formatString: "MMMM Do, YYYY")
      lastUpdated(formatString: "MMMM Do, YYYY")
      body {
        childMarkdownRemark {
          html
        }
      }
      body2 {
        childMarkdownRemark {
          html
        }
      }
      courseLinks {
        id
        courseTitle
        courseExcerpt
        courseUrl
        courseImage {
          file {
            url
          }
        }
      }
      relatedPosts {
        blogTitle
        excerpt
        publishDate(formatString: "MMMM Do, YYYY")
        slug
        popular
        tags {
          value
          label
          tagSlug
          isNewsletterTag
        }
        author {
          profileImage {
            file {
              url
            }
          }
          authorName
        }
        childContentfulBlogPostBodyTextNode {
          childMarkdownRemark {
            fields {
              readingTime {
                text
              }
            }
          }
        }
        heroImage {
          gatsbyImageData(placeholder: BLURRED, width: 400)
        }
      }
    }
  }
`
